import { useEffect, useState } from "react";
import { Lock, Mail, Phone, Trash2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "k29_admin_token";

function getInitialToken() {
  const url = new URLSearchParams(window.location.search).get("token");
  if (url) {
    try { localStorage.setItem(TOKEN_KEY, url); } catch {}
    return url;
  }
  try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
}

export default function AdminLeads() {
  const [token, setToken] = useState(getInitialToken);
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchLeads = async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/leads?token=${encodeURIComponent(token)}`);
      if (res.status === 401) {
        setError("Onjuiste token. Probeer opnieuw.");
        setItems(null);
        try { localStorage.removeItem(TOKEN_KEY); } catch {}
        return;
      }
      if (!res.ok) throw new Error("Server fout");
      const data = await res.json();
      setItems(data.items || []);
      try { localStorage.setItem(TOKEN_KEY, token); } catch {}
    } catch (e) {
      setError(e.message || "Fout");
    } finally {
      setLoading(false);
    }
  };

  const deleteLead = async (id) => {
    if (!confirm("Verwijderen?")) return;
    const res = await fetch(`${API}/leads/${id}?token=${encodeURIComponent(token)}`, {
      method: "DELETE",
    });
    if (res.ok) setItems((items || []).filter((it) => it.id !== id));
  };

  useEffect(() => {
    if (token) fetchLeads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!token || items === null) {
    return (
      <div className="flex min-h-[100svh] items-center justify-center bg-brand-bg px-6">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-2 text-brand-moss">
            <Lock size={16} />
            <span className="overline">Admin</span>
          </div>
          <h1 className="mt-4 font-serif text-4xl text-brand-ink">Leads</h1>
          <p className="mt-3 text-sm text-brand-muted">Geef je admin-token om de aanvragen te bekijken.</p>
          <input
            type="password"
            data-testid="admin-token-input"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Admin token"
            className="mt-6 w-full border border-brand-border bg-brand-bg px-4 py-3 text-sm focus:border-brand-moss focus:outline-none"
          />
          {error && <p className="mt-3 text-sm text-red-700">{error}</p>}
          <button
            data-testid="admin-login-btn"
            onClick={fetchLeads}
            disabled={loading || !token}
            className="mt-4 w-full bg-brand-moss px-6 py-3 text-sm font-medium text-white disabled:opacity-60"
          >
            {loading ? "Bezig…" : "Inloggen"}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[100svh] bg-brand-bg">
      <header className="border-b border-brand-border bg-brand-surface">
        <div className="mx-auto flex max-w-4xl flex-col gap-2 px-6 py-6 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="overline">Admin</p>
            <h1 className="mt-1 font-serif text-3xl text-brand-ink">Leads · {items.length}</h1>
          </div>
          <div className="flex gap-2">
            <button
              data-testid="admin-refresh"
              onClick={fetchLeads}
              className="inline-flex items-center gap-2 border border-brand-border bg-brand-bg px-4 py-2 text-sm hover:bg-brand-surface"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Ververs
            </button>
            <button
              onClick={() => {
                try { localStorage.removeItem(TOKEN_KEY); } catch {}
                setToken("");
                setItems(null);
              }}
              className="text-sm text-brand-muted underline"
            >
              Uitloggen
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        {items.length === 0 ? (
          <div className="border border-dashed border-brand-border p-12 text-center text-brand-muted">
            Nog geen aanvragen ontvangen.
          </div>
        ) : (
          <ul data-testid="leads-list" className="space-y-4">
            {items.map((lead) => (
              <li
                key={lead.id}
                data-testid={`lead-${lead.id}`}
                className="border border-brand-border bg-brand-surface p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-serif text-2xl text-brand-ink">{lead.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-wider text-brand-moss">
                      {lead.subject || "Stel een vraag"} ·{" "}
                      {new Date(lead.created_at).toLocaleString("nl-NL", {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => deleteLead(lead.id)}
                    className="text-brand-muted hover:text-red-600"
                    aria-label="Verwijder"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-4 text-sm">
                  <a
                    href={`mailto:${lead.email}?subject=Re: ${encodeURIComponent(lead.subject || "Kempenaar 29")}`}
                    className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-moss"
                  >
                    <Mail size={14} /> {lead.email}
                  </a>
                  {lead.phone && (
                    <a
                      href={`tel:${lead.phone}`}
                      className="inline-flex items-center gap-2 text-brand-ink hover:text-brand-moss"
                    >
                      <Phone size={14} /> {lead.phone}
                    </a>
                  )}
                </div>

                <p className="mt-4 whitespace-pre-wrap text-sm leading-relaxed text-brand-ink">
                  {lead.message}
                </p>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
